/**
 * File: useBooksMutations.ts
 * Path: apps/admin/src/shared/hooks/useBooksMutations.ts
 * Description: Mutation hooks split out of useBooks.ts.
 */

import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  byJournalOrder,
  type BankLineRecord,
  type Journal,
} from "@idk/config";
import { toast } from "@idk/ui";
import { accountingRepo, type BooksCommand } from "../services/accountingService";
import { booksKey } from "./useBooksQueries";

const profileOf = (command: BooksCommand, fallback: string) =>
  "profileId" in command ? command.profileId : fallback;
/** The bank lines a command changes, or null when it isn't a bank-line command. */
export const bankLinesTouched = (command: BooksCommand): string[] | null => {
  switch (command.action) {
    case "postBankLine":
    case "renameBankLine":
      return [command.lineId];
    case "postBankGroup":
    case "setBankLinesOff":
      return command.lineIds;
    case "postBankLines":
      return command.items.map((item) => item.lineId);
    default:
      return null;
  }
};
/**
 * After sorting bank lines, reads back just those lines and any journal they
 * now point at that isn't cached yet, and patches both lists in the cache.
 * The rest of the books is marked stale as usual. Reading every bank line and
 * every journal again after each line was most of the admin's database reads.
 */
const patchBankChange = async (
  queryClient: QueryClient,
  profileId: string,
  lineIds: string[],
): Promise<void> => {
  const linesKey = [...booksKey(profileId), "bankLines"];
  const journalsKey = [...booksKey(profileId), "journals"];
  const cachedLines = queryClient.getQueryData<BankLineRecord[]>(linesKey);
  const cachedJournals = queryClient.getQueryData<Journal[]>(journalsKey);
  const fresh = await accountingRepo.getBankLines(profileId, [...new Set(lineIds)]);
  if (cachedLines) {
    const byId = new Map(fresh.map((line) => [line.id, line]));
    queryClient.setQueryData<BankLineRecord[]>(linesKey, cachedLines.map((line) => byId.get(line.id) ?? line));
  }
  if (cachedJournals) {
    const known = new Set(cachedJournals.map((j) => j.id));
    const added = [...new Set(fresh.flatMap((line) => (line.journalId ? [line.journalId] : [])))].filter(
      (id) => !known.has(id),
    );
    if (added.length > 0) {
      const journals = await accountingRepo.getJournals(profileId, added);
      queryClient.setQueryData<Journal[]>(journalsKey, [...cachedJournals, ...journals].sort(byJournalOrder));
    }
  }
};
/** Sends one change to the books; the server's refusal is shown as it was worded. */
export const useBooksCommand = (profileId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: BooksCommand) => accountingRepo.run(command),
    onSuccess: async (_result, command) => {
      const profile = profileOf(command, profileId);
      const lineIds = bankLinesTouched(command);
      if (!lineIds) {
        void queryClient.invalidateQueries({ queryKey: booksKey(profile) });
        return;
      }
      try {
        await patchBankChange(queryClient, profile, lineIds);
        void queryClient.invalidateQueries({
          queryKey: booksKey(profile),
          predicate: (q) => q.queryKey[2] !== "bankLines" && q.queryKey[2] !== "journals",
        });
      } catch {
        // Couldn't read the changed lines back: fall back to reading everything.
        void queryClient.invalidateQueries({ queryKey: booksKey(profile) });
      }
    },
    onError: (error) => {
      console.error("[Accounting] change refused:", error);
      toast.error(error instanceof Error ? error.message : "The books couldn't be changed.");
    },
  });
};
